import { useMemo, useState } from "react";
import moment from "moment";
import { Chart } from "react-google-charts";
import { Dashboard, Select } from "components";
import useGet from "hooks/useGet";

const range_options = [
  { label: "30 dni", value: "30_days" },
  { label: "14 dni", value: "14_days" },
  { label: "7 dni", value: "7_days" },
];
const COLORS = [
  "#5D78FF",
  "#FF5D78",
  "#FFC15D",
  "#5DFF78",
  "#78FFD5",
  "#D578FF",
];

const columns = [
  {
    name: "Kliknięcia",
    key: "total_clicks",
  },
  {
    name: "Wyświetlenia",
    key: "total_views",
  },
  {
    name: "Koszt",
    key: "total_cost",
  },
  {
    name: "Sprzedaż",
    key: "sold_amount",
  },
  {
    name: "Średnie CPC",
    key: "average_cpc",
  },
  {
    name: "ROAS",
    key: "roas",
  },
];

const columns_obj = columns.reduce((acc, acu) => {
  acc[acu.key] = acu.name;

  return acc;
}, {});

const columns_options = columns.map(({ name, key }) => ({
  label: name,
  value: key,
}));

const CampaignStatsChart = ({
  channel_id,
  channel_campaign_id,
  channel_campaign_group_id,
  product_id,
  default_interval = "30_days",
  default_columns = ["total_clicks", "roas"],
}) => {
  const [interval_query, setIntervalQuery] = useState(default_interval);
  const [active_columns, setActiveColumns] = useState(default_columns);

  const { is_loading, data } = useGet({
    query: [
      "channel_campaign_group_stats",
      channel_campaign_group_id,
      product_id,
      interval_query,
    ],
    path: [
      "channels",
      channel_id,
      "channel_campaigns",
      channel_campaign_id,
      ...(channel_campaign_group_id
        ? ["channel_campaign_groups", channel_campaign_group_id]
        : []),
      "stats",
    ].join("/"),
    search: {
      interval: interval_query,
      product_id,
    },
  });

  const active_columns_names = useMemo(
    () => active_columns.map((item) => columns_obj[item]),
    [active_columns],
  );

  const labels = useMemo(
    () => ["Data", ...active_columns.map((key) => columns_obj[key])],
    [active_columns],
  );

  const chart_data = useMemo(() => {
    if (is_loading || !data?.length) {
      return [
        labels,
        [moment().format("DD/MM/YYYY"), ...active_columns.map(() => 0)],
      ];
    }

    return [
      labels,
      ...data.map(({ date, ...item }) => [
        moment(date).format("DD/MM/YYYY"),
        ...active_columns.map((key) => Number(item[key] ?? 0)),
      ]),
    ];
  }, [is_loading, data, interval_query, active_columns]);

  const series = active_columns.map((_, index) => ({
    targetAxisIndex: index,
    color: COLORS[index % COLORS.length],
  }));

  const vAxes = active_columns_names.reduce((acc, name, index) => {
    if (index === 0) {
      acc[index] = {
        title: name,
        textPosition: "left",
        viewWindow: { min: 0 },
        gridlines: { color: "#eee" }, // siatka tylko dla pierwszej kolumny
      };
    } else if (index === 1) {
      acc[index] = {
        title: name,
        textPosition: "right",
        viewWindow: { min: 0 },
        gridlines: { color: "transparent" }, // brak siatki
      };
    } else {
      acc[index] = {
        title: "",
        textPosition: "none",
        gridlines: { color: "transparent" },
      };
    }
    return acc;
  }, {});

  return (
    <Dashboard
      header={{
        title: "Statystyki",
        aside: (
          <>
            <div className="mr-10" style={{ width: 400 }}>
              <Select
                isMulti
                label="Kolumny"
                handleChange={(value) => setActiveColumns(value)}
                options={columns_options}
                value={columns_options.filter(({ value }) =>
                  active_columns.includes(value),
                )}
              />
            </div>
            <div style={{ width: 200 }}>
              <Select
                label="Zakres"
                handleChange={(value) => setIntervalQuery(value)}
                options={range_options}
                value={range_options.find(
                  ({ value }) => value === interval_query,
                )}
              />
            </div>
          </>
        ),
      }}
    >
      <Chart
        chartType="LineChart"
        loader={null}
        width="100%"
        height="500px"
        data={chart_data}
        options={{
          chartArea: { width: "88%" },
          hAxis: {
            textStyle: { fontSize: 8, color: "#595D6E" },
          },
          focusTarget: "category",
          tooltip: {
            trigger: "focus",
          },
          legend: {
            position: "top",
          },
          series,
          vAxes,
          // curveType: "function",
        }}
      />
    </Dashboard>
  );
};

export default CampaignStatsChart;
